import { useEffect, useRef } from 'react'
import { ProductCarouselSlide } from 'shared-types'
import { convertProductImageUrl, getSlideSrcSet } from '~/helpers'
import { usePage } from '~/hooks'

interface UsePreloadSlideImagesOptions {
  slides: ProductCarouselSlide[]
  selectedIndex: number
  srcSetWidths: number[]
  sizes: string
  enabled: boolean
}

// Warms the browser cache with every image slide other than the selected one,
// so paging through the gallery does not wait on a network round trip. Runs
// once the page is idle so it never competes with the visible image, and works
// outwards from the selected slide so the neighbours land first.
export const usePreloadSlideImages = ({
  slides,
  selectedIndex,
  srcSetWidths,
  sizes,
  enabled,
}: UsePreloadSlideImagesOptions) => {
  const { siteCode } = usePage()
  // Read through a ref so paging does not restart the preload; the order only
  // matters for the first pass, and later runs would be cache hits anyway.
  const selectedIndexRef = useRef(selectedIndex)
  selectedIndexRef.current = selectedIndex

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    const startIndex = selectedIndexRef.current
    const targets = slides
      .map((slide, index) => {
        return { slide, index }
      })
      .filter(({ slide, index }) => {
        return slide.type === 'image' && !!slide.src && index !== startIndex
      })
      .sort((a, b) => {
        return Math.abs(a.index - startIndex) - Math.abs(b.index - startIndex)
      })

    if (targets.length === 0) {
      return undefined
    }

    // The elements are never attached; the browser carries each request to
    // completion once src is set, whether or not anything still refers to it.
    const preload = () => {
      for (const { slide } of targets) {
        const src = convertProductImageUrl(slide.src, siteCode)
        // window.Image, not the ~/components/Image component.
        const image = new window.Image()

        // sizes before srcset, so the candidate is chosen with the right hint
        // the moment srcset is set.
        image.sizes = sizes
        image.srcset = getSlideSrcSet(src, srcSetWidths) ?? ''
        image.src = src
      }
    }

    const hasIdleCallback = typeof window.requestIdleCallback === 'function'
    const handle = hasIdleCallback
      ? window.requestIdleCallback(preload)
      : window.setTimeout(preload, 0)

    return () => {
      if (hasIdleCallback) {
        window.cancelIdleCallback(handle)
      } else {
        window.clearTimeout(handle)
      }
    }
  }, [slides, srcSetWidths, sizes, siteCode, enabled])
}
